import { useRef, useState } from 'react'
import { ContentPanel, Editor, Icon, Slider, Toolbar } from '../../components'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Button, ButtonGroup, Form } from 'react-bootstrap'
import { useDocuments, useSaveStoryMutation } from '../../hooks'
import {
  faAsterisk,
  faEye,
  faFile,
  faSave,
  faTimesCircle
} from '@fortawesome/free-solid-svg-icons'
import { DocumentBrowser } from '../documents/DocumentBrowser'

export const StoryEdit = ({
  category,
  story,
  onToggleEdit,
  title,
  setTitle,
  content,
  setContent,
  changed
}) => {
  const { tenantUrlSlug, affiliationUrlSlug, categoryUrlSlug } = useParams()
  const navigate = useNavigate()
  const { documents } = useDocuments()
  const editorRef = useRef(null)
  const [showFiles, setShowFiles] = useState()

  const { mutate: saveStory } = useSaveStoryMutation()
  const onSave = () =>
    saveStory(
      {
        categoryId: category.id,
        storyId: story.id,
        title,
        content
      },
      {
        onSuccess: (data) => {
          onToggleEdit()
          navigate(
            `/${tenantUrlSlug}/${affiliationUrlSlug}/${categoryUrlSlug}/${data.id}`
          )
        }
      }
    )

  const onToggleFiles = () => setShowFiles(!showFiles)

  const handleTitleChange = (e) => setTitle(e.target.value)

  const handleFocusEditor = (e) =>
    editorRef.current && editorRef.current.focus()

  return (
    <>
      <Toolbar title={category.name} />
      <ContentPanel className='story-panel'>
        <div className='d-flex justify-content-between align-items-center mb-3'>
          <h2>Create / Edit Story</h2>
          <ButtonGroup>
            <Button
              as={Link}
              to={`/${tenantUrlSlug}/${affiliationUrlSlug}/${categoryUrlSlug}`}
              variant='link'
            >
              <Icon icon={faTimesCircle} size='lg' />
              Discard
            </Button>
            {documents ? (
              <Button
                onClick={onToggleFiles}
                aria-label='Files'
                title='Show the slide-out document’s file browser.'
                variant='link'
              >
                <Icon icon={faFile} size='lg' /> Files
              </Button>
            ) : null}
            <Button
              onClick={onToggleEdit}
              aria-label='Preview'
              title='Show a preview of what the page will look like to viewers.'
              variant='link'
              disabled={!title || !content}
            >
              <Icon icon={faEye} size='lg' /> Preview
            </Button>
            <Button
              onClick={onSave}
              title='Publish'
              variant='link'
              disabled={!title || !content || !changed}
            >
              <Icon icon={faSave} size='lg' />
              {changed ? '* ' : null} Publish
            </Button>
          </ButtonGroup>
        </div>
        <Form.Group controlId='story-title'>
          <Form.Label>
            Title&nbsp;
            <Icon icon={faAsterisk} size='sm' className='text-secondary' />
          </Form.Label>
          <Form.Text className='text-muted'>
            Please write a clear, concise headline. This becomes the card title
            and the screen reader name. Aim for under 80 characters (max 200).
          </Form.Text>
          <Form.Control
            required
            maxLength={200}
            value={title}
            onChange={handleTitleChange}
          />
        </Form.Group>
        <Form.Group controlId='story-content'>
          <Form.Label onClick={handleFocusEditor}>
            Body&nbsp;
            <Icon icon={faAsterisk} size='sm' className='text-secondary' />
          </Form.Label>
          <Form.Text className='text-muted'>
            Please use headings to organize content. Add alternative text for
            every image (leave the alt field empty only if the image is
            decorative). Don’t underline text for emphasis. Only links should be
            underlined. Use descriptive link text (e.g., ‘Download the user
            guide’).
          </Form.Text>
          <Editor
            ref={editorRef}
            content={content}
            onContentChange={setContent}
          />
        </Form.Group>
      </ContentPanel>
      {documents ? (
        <Slider visible={showFiles}>
          <Button variant='link' onClick={onToggleFiles}>
            <Icon icon={faTimesCircle} size='lg' /> Close
          </Button>
          <DocumentBrowser documents={documents} />
        </Slider>
      ) : null}
    </>
  )
}
